import Keycloak from "keycloak-js";

export const ADMIN_PATH = "/opredelitel/admin";
export const DEFAULT_ADMIN_PATH = `${ADMIN_PATH}/blocks`;
export const CALLBACK_PATH = "/kc/callback";
export const RETURN_TO_KEY = "kc_return_to";

// In dev the callback is served from the site root, in production the SPA
// lives under /opredelitel. Register both routes so the same build works in
// both environments.
export const CALLBACK_ROUTES = ["/kc/callback", "/opredelitel/kc/callback"];

const APP_URL = (process.env.REACT_APP_URL || window.location.origin).replace(
  /\/+$/,
  "",
);

const KEYCLOAK_CLIENT_ID = process.env.REACT_APP_KEYCLOAK_CLIENT_ID;

export const REQUIRED_ROLE =
  process.env.REACT_APP_KEYCLOAK_REQUIRED_ROLE || "can_access";

/**
 * The role lives in the client's resource access:
 * token.resource_access[clientId].roles.
 */
export function hasRequiredRole(keycloak) {
  const roles =
    keycloak?.tokenParsed?.resource_access?.[KEYCLOAK_CLIENT_ID]?.roles || [];
  return roles.includes(REQUIRED_ROLE);
}

export function isAdminPath(pathname) {
  return pathname === ADMIN_PATH || pathname.startsWith(`${ADMIN_PATH}/`);
}

function isCallbackPath(pathname) {
  return CALLBACK_ROUTES.includes(pathname);
}

/**
 * Keycloak should only be initialized when the current page needs it.
 *
 * In particular, running `check-sso` on the public site makes keycloak-js
 * redirect anonymous visitors to Keycloak (and then to the callback page),
 * which would break the public part of the site. The public pages therefore
 * never initialize Keycloak; the provider is mounted lazily on the first
 * admin/callback navigation.
 */
export function shouldInitKeycloak(pathname) {
  return isAdminPath(pathname) || isCallbackPath(pathname);
}

function readReturnTo() {
  try {
    return sessionStorage.getItem(RETURN_TO_KEY);
  } catch {
    return null;
  }
}

function writeReturnTo(value) {
  try {
    sessionStorage.setItem(RETURN_TO_KEY, value);
  } catch {
    // Storage can be unavailable (privacy mode); deep-link restore is optional.
  }
}

function removeReturnTo() {
  try {
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    // Ignore unavailable storage.
  }
}

export function rememberReturnTo() {
  const { pathname, search, hash } = window.location;
  if (isAdminPath(pathname)) {
    writeReturnTo(`${pathname}${search}${hash}`);
  }
}

export function clearReturnTo() {
  removeReturnTo();
}

export function consumeReturnTo() {
  const value = readReturnTo();
  removeReturnTo();

  if (!value) return DEFAULT_ADMIN_PATH;
  if (value.startsWith("//")) return DEFAULT_ADMIN_PATH;
  if (value !== ADMIN_PATH && !value.startsWith(`${ADMIN_PATH}/`)) {
    return DEFAULT_ADMIN_PATH;
  }

  return value;
}

rememberReturnTo();

const keycloak = new Keycloak({
  url: process.env.REACT_APP_KEYCLOAK_BASE_URL,
  realm: process.env.REACT_APP_KEYCLOAK_REALM,
  clientId: KEYCLOAK_CLIENT_ID,
});

export function createKeycloakInitOptions() {
  return {
    // Any page that mounts the provider requires authentication. With a valid
    // authorization response keycloak-js processes the callback before onLoad.
    onLoad: "login-required",
    checkLoginIframe: false,
    enableLogging: process.env.NODE_ENV !== "production",
    pkceMethod: "S256",
    redirectUri: `${APP_URL}${CALLBACK_PATH}`,
  };
}

export const keycloakInitOptions = createKeycloakInitOptions();

export function login() {
  rememberReturnTo();
  keycloak.login({ redirectUri: keycloakInitOptions.redirectUri });
}

export function logout() {
  clearReturnTo();
  keycloak.logout({ redirectUri: keycloakInitOptions.redirectUri });
}

export default keycloak;
